import { Timestamp } from "@bufbuild/protobuf";
import {
  type CancelNotificationByPersonalInformationRequest,
  CancelNotificationByPersonalInformationResponse,
  type CreateNotificationByPersonalInformationRequest,
  CreateNotificationByPersonalInformationResponse,
  type CreateNotificationRequest,
  CreateNotificationResponse,
  type GetNotificationByPersonalInformationHistoriesRequest,
  GetNotificationByPersonalInformationHistoriesResponse,
  type GetNotificationHistoriesRequest,
  GetNotificationHistoriesResponse,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import {
  NotificationByPersonalInformationHistory,
  NotificationHistory,
  NotificationTemplate,
  PersonalInformationForNotification,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";

import { nextPrefixedId, simulateDelay } from "./utils";

const createButtonTemplate = (text: string, href: string) => JSON.stringify({
  version: "1.0",
  variant: "primary",
  text,
  href,
  serviceId: "",
});

export const mockCreateNotification = async (
  _req: CreateNotificationRequest,
): Promise<CreateNotificationResponse> => {
  await simulateDelay();
  return new CreateNotificationResponse({
    notificationHistoryId: nextPrefixedId(
      "create-notification",
      "notification-history-",
      { pad: 4 },
    ),
  });
};

export const mockCreateNotificationByPersonalInformation = async (
  _req: CreateNotificationByPersonalInformationRequest,
): Promise<CreateNotificationByPersonalInformationResponse> => {
  await simulateDelay();
  return new CreateNotificationByPersonalInformationResponse({
    notificationByPersonalInformationHistoryId: nextPrefixedId(
      "create-notification-personal",
      "notification-personal-history-",
      { pad: 4 },
    ),
  });
};

export const mockCancelNotificationByPersonalInformation = async (
  _req: CancelNotificationByPersonalInformationRequest,
): Promise<CancelNotificationByPersonalInformationResponse> => {
  await simulateDelay();
  return new CancelNotificationByPersonalInformationResponse({});
};

export const mockGetNotificationHistories = async (
  req: GetNotificationHistoriesRequest,
): Promise<GetNotificationHistoriesResponse> => {
  await simulateDelay();
  const titleTemplates = [
    "ポイント付与のお知らせ",
    "キャンペーンのお知らせ",
    "期限切れ間近のポイントについて",
    "新規店舗オープンのお知らせ",
    "アンケート回答のお願い",
    "システムメンテナンスのお知らせ",
    "お得な情報をお届け",
    "重要なお知らせ",
  ];

  const messageTemplates = [
    "ポイントが付与されました。詳細は通知をご確認ください。",
    "新しいキャンペーンが開始されました。今すぐチェック！",
    "ポイントの有効期限が近づいています。お早めにご利用ください。",
    "新規店舗がオープンしました！オープン記念で特別ポイントプレゼント中です。",
    "アンケートにご協力いただくとポイントを獲得できます。",
    "システムメンテナンスを実施します。ご不便をおかけしますが、よろしくお願いします。",
    "今週のお得情報！対象店舗でポイント倍率アップキャンペーン実施中です。",
    "サービスに関する重要な変更がございます。詳細をご確認ください。",
  ];

  const buttonTemplates = [
    "詳細を見る",
    "キャンペーンを見る",
    "ポイントを確認",
    "店舗情報を見る",
    "アンケートに回答",
    "詳細を確認",
    "お得情報を見る",
    "変更内容を確認",
  ];

  const histories: NotificationHistory[] = [];
  const baseDate = new Date("2024-12-15T10:00:00");

  for (let i = 0; i < 30; i++) {
    const templateIndex = i % titleTemplates.length;
    const date = new Date(baseDate);
    date.setDate(date.getDate() - i);
    date.setHours((i * 3) % 24);
    date.setMinutes((i * 7) % 60);
    const historyId = `notification-${(i + 1).toString().padStart(4, "0")}`;
    const serviceUrl = `https://example.com/notification/${historyId}`;

    histories.push(
      new NotificationHistory({
        id: historyId,
        userId: req.userId,
        createdAt: Timestamp.fromDate(date),
        original: new NotificationTemplate({
          locale: "ja",
          titleTemplate: titleTemplates[templateIndex],
          messageTemplate: messageTemplates[templateIndex],
          buttonTemplate: createButtonTemplate(buttonTemplates[templateIndex], serviceUrl),
          serviceUrlTemplate: serviceUrl,
        }),
        translations: i % 2 === 0
          ? [new NotificationTemplate({
              locale: "en",
              titleTemplate: `[English] ${titleTemplates[templateIndex]}`,
              messageTemplate: `[English] ${messageTemplates[templateIndex]}`,
              buttonTemplate: createButtonTemplate("View details", serviceUrl),
              serviceUrlTemplate: serviceUrl,
            })]
          : [],
      }),
    );
  }

  return new GetNotificationHistoriesResponse({
    notificationHistories: histories,
  });
};

export const mockGetNotificationByPersonalInformationHistories = async (
  _req: GetNotificationByPersonalInformationHistoriesRequest,
): Promise<GetNotificationByPersonalInformationHistoriesResponse> => {
  await simulateDelay();
  const titleTemplates = [
    "地域限定キャンペーン",
    "年代別特別オファー",
    "シニア向けサービスのご案内",
    "学生限定キャンペーン",
    "子育て応援キャンペーン",
    "地域振興ポイント配布",
    "季節限定イベントのお知らせ",
    "特定地域向けアンケート",
  ];

  const messageTemplates = [
    "お住まいの地域限定！特別ポイントプレゼント中",
    "特定の年齢層の方へ、特別なご案内です",
    "シニアの皆様へ、お得なサービスをご用意しました",
    "学生の皆様必見！学割キャンペーン実施中です",
    "子育て世代を応援！特別ポイントキャンペーン",
    "地域振興のため、ポイントを配布します",
    "この季節だけの特別イベントにご参加ください",
    "お住まいの地域の方へ、アンケートのお願い",
  ];

  const cityGroups = [
    ["宮城県仙台市青葉区", "宮城県仙台市宮城野区"],
    ["宮城県仙台市若林区", "宮城県仙台市太白区"],
    ["宮城県仙台市泉区"],
    ["宮城県石巻市", "宮城県塩竈市"],
    ["宮城県気仙沼市", "宮城県白石市"],
    ["宮城県名取市", "宮城県多賀城市"],
    ["宮城県岩沼市", "宮城県登米市"],
    ["宮城県栗原市", "宮城県東松島市", "宮城県大崎市"],
  ];

  const ageRanges = [
    { min: 18, max: 25 },
    { min: 20, max: 35 },
    { min: 30, max: 50 },
    { min: 40, max: 65 },
    { min: 50, max: 70 },
    { min: 60, max: 100 },
    { min: 18, max: 100 },
    { min: 25, max: 45 },
  ];

  const histories: NotificationByPersonalInformationHistory[] = [];
  const baseDate = new Date("2024-12-15T09:00:00");

  for (let i = 0; i < 30; i++) {
    const templateIndex = i % titleTemplates.length;
    const cityGroupIndex = i % cityGroups.length;
    const ageRangeIndex = i % ageRanges.length;

    const date = new Date(baseDate);
    date.setDate(date.getDate() - i);
    date.setHours((i * 5) % 24);
    date.setMinutes((i * 11) % 60);
    const historyId = `notification-personal-${(i + 1)
      .toString()
      .padStart(4, "0")}`;

    const scheduledAt = i % 4 === 0
      ? Timestamp.fromDate(new Date(Date.now() + (i + 1) * 60 * 60 * 1000))
      : undefined;
    const serviceUrl = `https://example.com/campaign/${historyId}`;
    const buttonText = templateIndex % 2 === 0 ? "詳細を確認" : "キャンペーンを見る";

    histories.push(
      new NotificationByPersonalInformationHistory({
        id: historyId,
        createdAt: Timestamp.fromDate(date),
        original: new NotificationTemplate({
          locale: "ja",
          titleTemplate: titleTemplates[templateIndex],
          messageTemplate: messageTemplates[templateIndex],
          buttonTemplate: createButtonTemplate(buttonText, serviceUrl),
          serviceUrlTemplate: serviceUrl,
        }),
        translations: i % 2 === 0
          ? [new NotificationTemplate({
              locale: "en",
              titleTemplate: `[English] ${titleTemplates[templateIndex]}`,
              messageTemplate: `[English] ${messageTemplates[templateIndex]}`,
              buttonTemplate: createButtonTemplate("View details", serviceUrl),
              serviceUrlTemplate: serviceUrl,
            })]
          : [],
        scheduledAt,
        personalInformation: new PersonalInformationForNotification({
          minAge: BigInt(ageRanges[ageRangeIndex].min),
          maxAge: BigInt(ageRanges[ageRangeIndex].max),
          addresses: cityGroups[cityGroupIndex],
        }),
      }),
    );
  }

  return new GetNotificationByPersonalInformationHistoriesResponse({
    notificationByPersonalInformationHistories: histories,
  });
};
